/*
  Reviews for the straps on a bundle page, not the whole store.

  sections/bundle-reviews-data.liquid emits, per page of the collection, each
  strap's Judge.me count, average, histogram and newest reviews. This fetches
  every page at once, adds the counts up, weights the average by review count,
  drops reviews seen twice (grouped products share them) and lists the rest
  with four and five star reviews first, newest first within each group.

  Nothing is shown until the numbers are in, and the whole section hides if
  the data cannot be read, so the page never shows a half summary.
*/
(function () {
  var root = document.querySelector('[data-bundle-reviews]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  var summaryEl = root.querySelector('[data-bundle-reviews-summary]');
  var listEl = root.querySelector('[data-bundle-reviews-list]');
  var moreEl = root.querySelector('[data-bundle-reviews-more]');
  var pageSize = parseInt(root.dataset.pageSize, 10) || 6;
  var lang = document.documentElement.lang || undefined;
  var STAR_PATH = 'm10 2.8 2.2 4.5 5 .7-3.6 3.5.85 4.95L10 14.1l-4.45 2.35.85-4.95L2.8 8l5-.7z';

  function label(name, values) {
    var text = root.dataset[name] || '';
    Object.keys(values || {}).forEach(function (key) {
      text = text.split('[' + key + ']').join(values[key]);
    });
    return text;
  }

  function node(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  }

  /* Same markup as snippets/bundle-stars.liquid, so the page's star styles apply. */
  function stars(rating, ariaLabel) {
    var wrap = node('span', 'bundle-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', ariaLabel);
    var whole = Math.floor(rating);
    var remainder = Math.round((rating - whole) * 100);
    for (var i = 1; i <= 5; i += 1) {
      var filled = i <= whole ? 100 : (i === whole + 1 ? remainder : 0);
      var star = node('span', 'bundle-stars__star');
      star.style.setProperty('--fill', filled + '%');
      star.innerHTML =
        '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="' + STAR_PATH + '" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>' +
        '<svg viewBox="0 0 20 20" class="bundle-stars__fill" aria-hidden="true"><path d="' + STAR_PATH + '" fill="currentColor"/></svg>';
      wrap.appendChild(star);
    }
    return wrap;
  }

  /*
    Judge.me stores some review text HTML-escaped ("I&#39;m"). A textarea decodes
    entities without parsing markup or running anything, and the result only
    ever goes into textContent.
  */
  var decoder = document.createElement('textarea');
  function decode(value) {
    if (!value) return '';
    decoder.innerHTML = String(value);
    return decoder.value;
  }

  function formatNumber(value) {
    try {
      return Number(value).toLocaleString(lang);
    } catch (error) {
      return String(value);
    }
  }

  /* Judge.me writes two date formats into the same field. */
  function toTime(value) {
    if (!value) return 0;
    var normalised = String(value).replace(' UTC', 'Z').replace(' ', 'T');
    var time = Date.parse(normalised);
    return isNaN(time) ? 0 : time;
  }

  function formatDate(time) {
    if (!time) return '';
    try {
      return new Date(time).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
      return new Date(time).toDateString();
    }
  }

  function fetchPages() {
    var count = parseInt(root.dataset.productCount, 10) || 50;
    var pages = Math.min(12, Math.max(1, Math.ceil(count / 50)));
    var base = root.dataset.url;
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    var work = [];
    for (var n = 1; n <= pages; n += 1) {
      work.push(fetch(base + sep + 'section_id=bundle-reviews-data&page=' + n, { headers: { Accept: 'text/html' } })
        .then(function (response) {
          if (!response.ok) throw new Error('bundle reviews ' + response.status);
          return response.text();
        })
        .then(function (text) {
          var open = text.indexOf('{');
          var close = text.lastIndexOf('}');
          if (open === -1 || close === -1) throw new Error('bundle reviews were not JSON');
          return JSON.parse(text.slice(open, close + 1));
        }));
    }
    return Promise.all(work);
  }

  function combine(bodies) {
    var total = 0;
    var weighted = 0;
    var histogram = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    var seen = {};
    var reviews = [];

    bodies.forEach(function (body) {
      (body.products || []).forEach(function (product) {
        var count = Number(product.count) || 0;
        var rating = parseFloat(product.rating) || 0;
        total += count;
        weighted += count * rating;

        (product.histogram || []).forEach(function (bar) {
          var key = Number(bar.rating);
          if (histogram[key] != null) histogram[key] += Number(bar.count) || 0;
        });

        (product.reviews || []).forEach(function (review) {
          if (!review || !review.id || seen[review.id]) return;
          seen[review.id] = true;
          review.time = toTime(review.date);
          review.title = decode(review.title);
          review.body = decode(review.body);
          review.name = decode(review.name);
          review.variant = decode(review.variant);
          review.product = decode(product.title);
          review.url = product.url;
          reviews.push(review);
        });
      });
    });

    /*
      Four and five stars first, then the rest, newest first within each. Lower
      ratings still show further down, and the summary above counts every review.
    */
    reviews.sort(function (a, b) {
      var aHigh = Number(a.rating) >= 4 ? 1 : 0;
      var bHigh = Number(b.rating) >= 4 ? 1 : 0;
      if (aHigh !== bHigh) return bHigh - aHigh;
      return b.time - a.time;
    });
    return { total: total, average: total ? weighted / total : 0, histogram: histogram, reviews: reviews };
  }

  function renderSummary(data) {
    var average = Math.round(data.average * 100) / 100;
    var score = node('div', 'bundle-reviews__score');
    score.appendChild(node('span', 'bundle-reviews__average', average.toFixed(2)));

    var detail = node('div', 'bundle-reviews__score-detail');
    detail.appendChild(stars(average, label('labelStars', { rating: average.toFixed(2), count: formatNumber(data.total) })));
    detail.appendChild(node('p', 'bundle-reviews__count', label('labelSummary', { count: formatNumber(data.total) })));
    score.appendChild(detail);

    var bars = node('ul', 'bundle-reviews__histogram');
    bars.setAttribute('role', 'list');
    [5, 4, 3, 2, 1].forEach(function (rating) {
      var count = data.histogram[rating] || 0;
      var share = data.total ? Math.round((count / data.total) * 1000) / 10 : 0;
      var row = node('li', 'bundle-reviews__histogram-row');
      row.setAttribute('aria-label', label('labelHistogram', { rating: rating, count: formatNumber(count) }));

      var rowLabel = node('span', 'bundle-reviews__histogram-label', String(rating));
      rowLabel.setAttribute('aria-hidden', 'true');
      var bar = node('span', 'bundle-reviews__bar');
      bar.setAttribute('aria-hidden', 'true');
      var fill = node('span', 'bundle-reviews__bar-fill');
      fill.style.setProperty('--share', share + '%');
      bar.appendChild(fill);
      var rowCount = node('span', 'bundle-reviews__histogram-count', formatNumber(count));
      rowCount.setAttribute('aria-hidden', 'true');

      row.appendChild(rowLabel);
      row.appendChild(bar);
      row.appendChild(rowCount);
      bars.appendChild(row);
    });

    summaryEl.textContent = '';
    summaryEl.appendChild(score);
    summaryEl.appendChild(bars);
  }

  function renderReview(review) {
    var item = node('li', 'bundle-review');
    var rating = Number(review.rating) || 0;

    var top = node('div', 'bundle-review__top');
    top.appendChild(stars(rating, label('labelStars', { rating: rating, count: 1 }).replace(/,.*$/, '')));
    if (review.time) {
      var date = node('time', 'bundle-review__date', formatDate(review.time));
      date.setAttribute('datetime', new Date(review.time).toISOString());
      top.appendChild(date);
    }
    item.appendChild(top);

    var author = node('p', 'bundle-review__author', review.name || '');
    if (review.verified) author.appendChild(node('span', 'bundle-review__verified', label('labelVerified')));
    item.appendChild(author);

    if (review.title) item.appendChild(node('p', 'bundle-review__title', review.title));
    if (review.body) item.appendChild(node('p', 'bundle-review__body', review.body));

    var photos = (review.photos || []).filter(Boolean);
    if (photos.length) {
      var gallery = node('div', 'bundle-review__photos');
      photos.slice(0, 4).forEach(function (src) {
        var img = node('img');
        img.src = src;
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = label('labelPhoto', { name: review.name || '' });
        gallery.appendChild(img);
      });
      item.appendChild(gallery);
    }

    if (review.url && review.product) {
      var link = node('a', 'bundle-review__product', review.variant ? review.product + ' · ' + review.variant : review.product);
      link.href = review.url;
      item.appendChild(link);
    }

    return item;
  }

  function start() {
    fetchPages()
      .then(combine)
      .then(function (data) {
        if (!data.total || !data.reviews.length) {
          root.hidden = true;
          return;
        }

        renderSummary(data);

        var shown = 0;
        function showNext() {
          var batch = data.reviews.slice(shown, shown + pageSize);
          var fragment = document.createDocumentFragment();
          batch.forEach(function (review) { fragment.appendChild(renderReview(review)); });
          listEl.appendChild(fragment);
          shown += batch.length;
          moreEl.hidden = shown >= data.reviews.length;
        }

        moreEl.addEventListener('click', showNext);
        showNext();
      })
      .catch(function (error) {
        root.hidden = true;
        if (window.slog) window.slog('custom', { message: 'bundle reviews failed', extra: { error: String(error && error.message || error) } });
      });
  }

  /* The data is below the builder and UGC, so wait until the shopper is near it. */
  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) {
        observer.disconnect();
        start();
      }
    }, { rootMargin: '800px 0px' });
    observer.observe(root);
  } else {
    start();
  }
})();
